/**
 * Confirm a Rapid Fusion cover card, or drop an agreement into a project Commercials folder.
 */

import React, { useEffect, useState } from 'react';
import { authFetch } from '../../utils/api';

const CHANNELS = [
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['remote', 'Remote'],
  ['onSite', 'On site'],
];

function emptyFields() {
  return {
    parties: '',
    cell: '',
    site: '',
    serial: '',
    warrantyStart: '',
    warrantyEnd: '',
    supportStart: '',
    supportEnd: '',
    hours: '',
    timezone: '',
    channels: { email: '', phone: '', remote: '', onSite: '' },
    included: '',
    excluded: '',
    responseTimes: '',
    raisers: '',
    afterEnd: '',
  };
}

function triToForm(value) {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
}

function formFrom(fields) {
  const next = emptyFields();
  if (!fields) return next;
  Object.keys(next).forEach((key) => {
    if (key === 'channels' || key === 'included' || key === 'excluded') return;
    next[key] = fields[key] || '';
  });
  CHANNELS.forEach(([key]) => {
    next.channels[key] = triToForm(fields.channels && fields.channels[key]);
  });
  next.included = (fields.included || []).join('\n');
  next.excluded = (fields.excluded || []).join('\n');
  return next;
}

function formToFields(form) {
  const channels = {};
  CHANNELS.forEach(([key]) => {
    const value = form.channels[key];
    channels[key] = value === 'yes' ? true : value === 'no' ? false : null;
  });
  return {
    ...form,
    channels,
    included: form.included,
    excluded: form.excluded,
  };
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Something went wrong. Try again.');
  return data;
}

export default function AgreementPanel({ orgId, orgName, onSaved }) {
  const [orgs, setOrgs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [pickedOrg, setPickedOrg] = useState(orgId || '');
  const [pickedName, setPickedName] = useState(orgName || '');
  const [pack, setPack] = useState(null);
  const [currentId, setCurrentId] = useState('');
  const [form, setForm] = useState(emptyFields());
  const [folderKey, setFolderKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/agreements/orgs')
      .then(readJson)
      .then((data) => { if (!cancelled) setOrgs(data.organizations || []); })
      .catch(() => {});
    authFetch('/api/agreements/folders')
      .then(readJson)
      .then((data) => { if (!cancelled) setFolders(data.folders || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setPickedOrg(orgId || '');
    setPickedName(orgName || '');
  }, [orgId, orgName]);

  useEffect(() => {
    if (!pickedOrg && !pickedName) {
      setPack(null);
      return undefined;
    }
    let cancelled = false;
    const query = new URLSearchParams();
    if (pickedOrg) query.set('orgId', pickedOrg);
    if (pickedName) query.set('orgName', pickedName);
    authFetch(`/api/agreements?${query.toString()}`)
      .then(readJson)
      .then((data) => {
        if (cancelled) return;
        setPack(data);
        const first = (data.agreements || [])[0];
        if (first) {
          setCurrentId(first.id);
          setForm(formFrom(first.fields));
        } else {
          setCurrentId('');
          setForm(emptyFields());
        }
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [pickedOrg, pickedName]);

  function chooseOrg(event) {
    const id = event.target.value;
    const found = orgs.find((org) => org.id === id);
    setPickedOrg(id);
    setPickedName(found ? found.name : '');
    setNotice('');
  }

  function chooseAgreement(event) {
    const id = event.target.value;
    setCurrentId(id);
    const found = (pack?.agreements || []).find((item) => item.id === id);
    setForm(formFrom(found && found.fields));
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setChannel(key, value) {
    setForm((prev) => ({ ...prev, channels: { ...prev.channels, [key]: value } }));
  }

  const current = (pack?.agreements || []).find((item) => item.id === currentId) || null;

  async function save(confirm) {
    if (!current) return;
    setBusy(true);
    setError('');
    try {
      const saved = await readJson(await authFetch(`/api/agreements/${encodeURIComponent(current.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: formToFields(form), confirm }),
      }));
      setPack((prev) => ({
        ...prev,
        agreements: (prev.agreements || []).map((item) => (item.id === saved.id ? saved : item)),
      }));
      setForm(formFrom(saved.fields));
      setNotice(confirm ? 'Cover card confirmed.' : 'Draft saved.');
      if (onSaved) onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function upload(file) {
    if (!file) return;
    if (!pickedOrg) {
      setError('Choose the organisation first.');
      return;
    }
    const folder = folders.find((item) => `${item.section}|${item.path}` === folderKey);
    if (!folder) {
      setError('Choose the project Commercials folder.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('orgId', pickedOrg);
      body.append('section', folder.section);
      body.append('folder', folder.path);
      const saved = await readJson(await authFetch('/api/agreements', { method: 'POST', body }));
      setPack((prev) => ({ ...(prev || { applies: true }), agreements: [saved, ...((prev && prev.agreements) || [])] }));
      setCurrentId(saved.id);
      setForm(formFrom(saved.fields));
      setNotice('Uploaded. Check the summary, then confirm it.');
      if (onSaved) onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (pack && pack.applies === false) {
    return <p className="support-meta">Service agreements are for Rapid Fusion customers.</p>;
  }

  return (
    <div className="support-agreement">
      {!orgId && (
        <label className="rf-label" htmlFor="agreement-org">Organisation</label>
      )}
      {!orgId && (
        <select id="agreement-org" className="rf-input" value={pickedOrg} onChange={chooseOrg}>
          <option value="">Choose</option>
          {orgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
        </select>
      )}
      {pack && (pack.agreements || []).length > 1 && (
        <label className="rf-label" htmlFor="agreement-which">
          Agreement
          <select id="agreement-which" className="rf-input" value={currentId} onChange={chooseAgreement}>
            {(pack.agreements || []).map((item) => (
              <option key={item.id} value={item.id}>{item.documentName || item.id}</option>
            ))}
          </select>
        </label>
      )}
      {current && (
        <p className="support-meta">
          {current.label}
          {current.documentId ? (
            <>
              {' · '}
              <a href={`/document-store?file=${encodeURIComponent(current.documentId)}`}>Open the file</a>
            </>
          ) : null}
        </p>
      )}
      {current && current.extractError && <p className="support-error">{current.extractError}</p>}
      {current && (
        <div className="support-agreement-fields">
          {[
            ['parties', 'Parties'],
            ['cell', 'Cell'],
            ['site', 'Site'],
            ['serial', 'Serial'],
            ['hours', 'Hours'],
            ['timezone', 'Timezone'],
            ['responseTimes', 'Response times, if the agreement states them'],
            ['raisers', 'Who may raise a ticket'],
            ['afterEnd', 'What happens after the end date'],
          ].map(([key, label]) => (
            <label key={key} className="rf-label" htmlFor={`agreement-${key}`}>
              {label}
              <input id={`agreement-${key}`} className="rf-input" value={form[key]} onChange={(event) => setField(key, event.target.value)} />
            </label>
          ))}
          {[
            ['warrantyStart', 'Warranty start'],
            ['warrantyEnd', 'Warranty end'],
            ['supportStart', 'Paid support start'],
            ['supportEnd', 'Paid support end'],
          ].map(([key, label]) => (
            <label key={key} className="rf-label" htmlFor={`agreement-${key}`}>
              {label}
              <input id={`agreement-${key}`} className="rf-input" type="date" value={form[key]} onChange={(event) => setField(key, event.target.value)} />
            </label>
          ))}
          {CHANNELS.map(([key, label]) => (
            <label key={key} className="rf-label" htmlFor={`agreement-channel-${key}`}>
              {label}
              <select id={`agreement-channel-${key}`} className="rf-input" value={form.channels[key]} onChange={(event) => setChannel(key, event.target.value)}>
                <option value="">Not stated</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          ))}
          <label className="rf-label" htmlFor="agreement-included">
            Included, one line each
            <textarea id="agreement-included" className="rf-input" rows={3} value={form.included} onChange={(event) => setField('included', event.target.value)} />
          </label>
          <label className="rf-label" htmlFor="agreement-excluded">
            Not included, one line each
            <textarea id="agreement-excluded" className="rf-input" rows={3} value={form.excluded} onChange={(event) => setField('excluded', event.target.value)} />
          </label>
          <span className="support-head-actions">
            <button type="button" className="rf-btn rf-btn-secondary" disabled={busy} onClick={() => save(false)}>Save draft</button>
            <button type="button" className="rf-btn rf-btn-primary" disabled={busy} onClick={() => save(true)}>Confirm cover card</button>
          </span>
        </div>
      )}
      <label className="rf-label" htmlFor="agreement-folder">Project Commercials folder</label>
      <select id="agreement-folder" className="rf-input" value={folderKey} onChange={(event) => setFolderKey(event.target.value)}>
        <option value="">Choose</option>
        {folders.map((folder) => (
          <option key={`${folder.section}|${folder.path}`} value={`${folder.section}|${folder.path}`}>
            {folder.path}
          </option>
        ))}
      </select>
      <label
        className="support-agreement-drop"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          upload(event.dataTransfer.files && event.dataTransfer.files[0]);
        }}
      >
        Drop a PDF or Word agreement here
        <input
          type="file"
          accept=".pdf,.doc,.docx,application/pdf"
          onChange={(event) => {
            upload(event.target.files && event.target.files[0]);
            event.target.value = '';
          }}
        />
      </label>
      {notice && <p className="support-meta">{notice}</p>}
      {error && <p className="support-error">{error}</p>}
    </div>
  );
}
